"use strict";
/**
 * 多语言 AI 翻译契约。
 *
 * 与页面/商品契约的关系：那边定义「内容长什么样」，这边定义「翻译这件事的状态」。
 * 刻意不把译文放进任何 Translation 类型里 —— 译文的真源永远是
 * `pages` / `page_blocks` / `*_translations` / `site_shell_translations`，
 * 这里只描述任务与台账。否则会出现「台账里的译文」和「页面里的译文」两份，
 * 而客户只会看到其中一份。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MAX_BATCH_SEGMENTS = exports.MAX_JOB_CHARS = exports.TRANSLATE_QUOTA_BY_PLAN = exports.TRANSLATION_REASON_LABEL = exports.TRANSLATION_REASONS = exports.TRANSLATION_UNIT_STATUSES = exports.TRANSLATION_JOB_STATUSES = exports.TRANSLATION_STRATEGIES = exports.TRANSLATION_TARGET_KINDS = void 0;
exports.translateQuotaForPlan = translateQuotaForPlan;
exports.isTranslationStrategy = isTranslationStrategy;
exports.isTranslationTargetKind = isTranslationTargetKind;
// ── 任务范围 ─────────────────────────────────────────────────────────
/** 一次翻译覆盖哪几类内容。shell = 页头页脚与导航（有覆盖层，见 0032） */
exports.TRANSLATION_TARGET_KINDS = ['page', 'product', 'category', 'shell'];
/**
 * 翻译策略。
 *
 * - missing：只翻「还没有译文」的（首次全站用这个，最便宜）
 * - stale：只翻「源文改过、译文过期」的（日常用这个）
 * - all：全部重翻（客户觉得整体质量不行时用；人工改过的仍默认跳过）
 *
 * 为什么不合并成「智能」一个选项：客户需要知道这次会花多少钱，
 * 而"智能"意味着系统替他决定，预估页面就说不清了。
 */
exports.TRANSLATION_STRATEGIES = ['missing', 'stale', 'all'];
// ── 任务状态 ─────────────────────────────────────────────────────────
exports.TRANSLATION_JOB_STATUSES = [
    'pending',
    'running',
    'succeeded',
    'partial',
    'failed',
    'canceled',
];
exports.TRANSLATION_UNIT_STATUSES = [
    'pending',
    'done',
    'review',
    'failed',
    'skipped',
];
/**
 * 需复核 / 失败 / 跳过的原因码。
 *
 * 用码不用中文句子：改文案不该改数据行，而且同一个码在控制台、日志、
 * 告警里要有完全一致的含义。
 */
exports.TRANSLATION_REASONS = [
    /** 型号（如 5-Axis / VMC850）在译文里找不到 */
    'model_no_number',
    /** 原文里的数字在译文中缺失或被换算 */
    'number_missing',
    /** 译文语言不对（阿语里没有阿拉伯字母、西语整段是英文） */
    'wrong_language',
    /** 模型返回空串 */
    'empty_output',
    /** 超长被截断 */
    'too_long_truncated',
    /** 长度突变（<0.3x 或 >3x） */
    'length_anomaly',
    /** 富文本标签结构与源不一致，已回退 */
    'tag_mismatch',
    /** 术语表命中但译文没用指定词 */
    'glossary_missed',
    /** 这条是人工译文，按策略跳过 */
    'human_edited',
    /** 源内容还是草稿，不翻 */
    'source_draft',
    /** 翻译期间有人编辑过目标内容，跳过以免覆盖 */
    'edited_during_translation',
    /** provider 报错（重试后仍失败） */
    'provider_error',
    /** 译文拿到了，但写进库时失败（约束冲突、连接中断等）。可重试 */
    'write_failed',
    /** 没配 provider / 没 Key */
    'no_provider',
    /** 值看着像链接/标识而不是文案（抽取阶段就跳过） */
    'not_copy',
];
/** 控制台把码翻成人话。放在契约里而不是 UI 里：接口文档、告警、日志要一致 */
exports.TRANSLATION_REASON_LABEL = {
    model_no_number: '译文里可能找不到原来的产品型号',
    number_missing: '原文的数字在译文里缺失',
    wrong_language: '译文语言不对',
    empty_output: '模型返回了空内容',
    too_long_truncated: '译文过长，已截断到字数上限',
    length_anomaly: '译文长度与原文相差过大',
    tag_mismatch: '富文本格式对不上，已回退原文',
    glossary_missed: '术语表里的词没有按指定译法翻译',
    human_edited: '这条是人工修改过的译文，按策略保留',
    source_draft: '源内容是草稿，不翻译',
    edited_during_translation: '翻译期间有人编辑过，已跳过以免覆盖',
    provider_error: '翻译服务返回错误',
    write_failed: '译文没写进库（这条可以重翻）',
    no_provider: '还没有配置翻译模型',
    not_copy: '这一项不是文案（链接/标识），不翻译',
};
// ── 额度 ─────────────────────────────────────────────────────────────
/**
 * 月度翻译字符额度（按套餐）。
 *
 * 与 `plans.quotas` 的关系：**不写进 plans 的 jsonb**，用代码默认值。
 * 理由：套餐表是整个计费链路的事实源，改它的数据要连带改账单页的展示逻辑；
 * 而翻译额度在 v1 是内置福利，不是一个可售的计量项。等真要卖额度时再迁移。
 *
 * 数量级的依据：一个 6 页 + 50 商品的站全量翻译约 1.5–2 万字符，
 * 所以 starter 的 10 万够翻 5 遍，一年正常的改稿量也用不完。
 */
exports.TRANSLATE_QUOTA_BY_PLAN = {
    starter: 100_000,
    growth: 500_000,
    business: 2_000_000,
};
/** 未知套餐按最低档算：宁可让客户来问，也不要给出一个我们自己没算过的额度 */
function translateQuotaForPlan(planCode) {
    return exports.TRANSLATE_QUOTA_BY_PLAN[planCode] ?? exports.TRANSLATE_QUOTA_BY_PLAN['starter'];
}
/** 单次任务字符上限。超过要求客户分批：一次跑 20 万字符的失败重跑代价太高 */
exports.MAX_JOB_CHARS = 200_000;
/** 单批最多多少段（provider 的 batchSize 会再夹一次） */
exports.MAX_BATCH_SEGMENTS = 20;
function isTranslationStrategy(value) {
    return typeof value === 'string' && exports.TRANSLATION_STRATEGIES.includes(value);
}
function isTranslationTargetKind(value) {
    return typeof value === 'string' && exports.TRANSLATION_TARGET_KINDS.includes(value);
}
//# sourceMappingURL=translation.js.map